import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import './SharingWithOthersView.css';

const SharingWithOthersView = ({ onClose, onManageShares }) => {
  const { token } = useAuth();
  const [sharingData, setSharingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedTable, setExpandedTable] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    fetchSharingData();
  }, []);

  const fetchSharingData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/shares/outgoing`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sharing data');
      }

      const data = await response.json();

      // Group shares by table
      const groupedByTable = data.reduce((acc, share) => {
        const tableId = share.table_id;
        if (!acc[tableId]) {
          acc[tableId] = {
            table_id: tableId,
            table_name: share.table_name,
            is_deleted: share.table_name === "Unknown",
            shares: []
          };
        }
        acc[tableId].shares.push(share);
        return acc;
      }, {});

      setSharingData(Object.values(groupedByTable));
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const revokeShare = async (shareId) => {
    if (!window.confirm('Are you sure you want to stop sharing this table?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/shares/${shareId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to revoke share');
      }

      // Refresh the sharing data
      await fetchSharingData();
    } catch (err) {
      alert(`Error revoking share: ${err.message}`);
    }
  };

  const revokeAllSharesForTable = async (tableId, tableName) => {
    if (!window.confirm(`Are you sure you want to revoke all shares for "${tableName}"?`)) {
      return;
    }

    const tableData = sharingData.find(t => t.table_id === tableId);
    if (!tableData) return;

    try {
      // Revoke each share
      for (const share of tableData.shares) {
        await fetch(`${API_URL}/api/shares/${share.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      // Refresh the sharing data
      await fetchSharingData();
    } catch (err) {
      alert(`Error revoking shares: ${err.message}`);
    }
  };

  return (
    <div className="sharing-overlay" onClick={onClose}>
      <div className="sharing-panel" onClick={(e) => e.stopPropagation()}>
        <div className="sharing-header">
          <h2>Tables I'm Sharing with Others</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="sharing-content">
          {loading && <div className="loading">Loading sharing information...</div>}
          {error && <div className="error-message">{error}</div>}

          {!loading && !error && sharingData.length === 0 && (
            <div className="no-shares">
              <p>You haven't shared any tables yet.</p>
            </div>
          )}

          {!loading && !error && sharingData.length > 0 && (
            <div className="tables-list">
              {sharingData.map((tableData) => (
                <div key={tableData.table_id} className="shared-table-group">
                  <div className="table-group-header">
                    <h3>
                      {tableData.table_name}
                      {tableData.is_deleted && (
                        <span style={{ color: '#ef4444', fontSize: '0.85em', marginLeft: '8px' }}>
                          (Table Deleted)
                        </span>
                      )}
                    </h3>
                    {tableData.is_deleted && (
                      <button
                        className="btn-manage-shares"
                        style={{ background: '#ef4444' }}
                        onClick={() => revokeAllSharesForTable(tableData.table_id, tableData.table_name)}
                      >
                        Revoke All Shares
                      </button>
                    )}
                  </div>
                  <div className="shares-list">
                    {tableData.shares.map((share) => (
                      <div key={share.id} className="share-item">
                        <div className="share-user-info">
                          <span className="username">{share.shared_with_username}</span>
                          <span className={`permission-badge ${share.permission}`}>
                            {share.permission === 'write' ? 'Can Edit' : 'Read Only'}
                          </span>
                        </div>
                        <button
                          className="btn-revoke-share"
                          onClick={() => revokeShare(share.id)}
                          style={{
                            padding: '4px 12px',
                            background: 'var(--bg-secondary)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Stop Sharing
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharingWithOthersView;
